// Auto-scroll & infinite-scroll hook for chat-style containers.
//
// The hook lives at the *top* of a scrollable container (`el.parentElement`).
//
// Behaviour:
// - Auto-follows new content when the user is already at (or near) the bottom.
// - Does NOT yank the user back to bottom while they're reading history.
// - Coalesces every scroll request into a single `requestAnimationFrame`,
//   so streaming dozens of chunks per second produces at most one scrollTop
//   write per frame (prevents scrollbar flashing on macOS).
// - Triggers `load_more` when the user scrolls near the top.
//
// The container also receives `scrollbar-gutter: stable` so the scrollbar
// gutter doesn't pop in/out as content grows.

const STICKY_THRESHOLD_PX = 32;
const TOP_LOAD_MORE_THRESHOLD_PX = 10;

export default {
  ChatScroll: {
    mounted() {
      this.scrollContainer = this.scrollableParent(this.el);
      if (!this.scrollContainer) return;

      // Reserve scrollbar gutter so the layout doesn't shift when overflow
      // toggles. This is what makes the scrollbar appear to "flash" on every
      // message in macOS overlay-scrollbar mode.
      try {
        this.scrollContainer.style.scrollbarGutter = 'stable';
      } catch (_) {
        /* older browsers */
      }
      this.scrollContainer.style.overflowAnchor = 'none';

      this.isLoading = false;
      this._lastUserScrollAt = 0;
      this._lastScrollTop = this.scrollContainer.scrollTop;
      this._lastScrollHeight = this.scrollContainer.scrollHeight;
      // Counter (not boolean) so overlapping programmatic scrolls don't race.
      this._programmaticScroll = 0;
      // Start anchored to the bottom of the chat.
      this._stickToBottom = true;
      this._scrollScheduled = false;
      this._boundHandleScroll = this.handleScroll.bind(this);

      const cooldownMs = parseInt(this.el.dataset.userScrollCooldownMs, 10);
      this._userScrollCooldownMs = Number.isNaN(cooldownMs) ? 2000 : cooldownMs;

      this.scrollContainer.addEventListener('scroll', this._boundHandleScroll, {
        passive: true,
      });

      // Auto-follow streaming content: when the DOM grows and the user is at
      // the bottom, stay at the bottom. Coalesced via rAF.
      this._mutationObserver = new MutationObserver(() => {
        if (this._stickToBottom) this.scheduleScrollToBottom();
      });
      this._mutationObserver.observe(this.scrollContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      // Container resizes (sidebar width, viewport, virtual keyboard) and content
      // reflowing can also pull us off the bottom; re-anchor in that case.
      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(() => {
          if (this._stickToBottom) this.scheduleScrollToBottom();
        });
        this._resizeObserver.observe(this.scrollContainer);
        if (this.scrollContainer.firstElementChild) {
          this._resizeObserver.observe(this.scrollContainer.firstElementChild);
        }
      }

      this.handleEvent('scroll_to_bottom', (payload = {}) => {
        const force = !!(payload && payload.force);
        if (force) {
          // Forced scrolls (e.g. action-required prompts) override user
          // cooldown and re-engage stick-to-bottom mode.
          this._stickToBottom = true;
          this.scheduleScrollToBottom();
          return;
        }

        // Soft scroll request: only follow if user is at the bottom and
        // hasn't recently scrolled away.
        const sinceUserScroll = Date.now() - this._lastUserScrollAt;
        if (sinceUserScroll < this._userScrollCooldownMs) return;
        if (this._stickToBottom) this.scheduleScrollToBottom();
      });

      // Initial anchor.
      this.scheduleScrollToBottom();
    },

    handleScroll() {
      const sc = this.scrollContainer;
      const { scrollTop, scrollHeight } = sc;
      const movedUp = scrollTop < this._lastScrollTop && scrollHeight >= this._lastScrollHeight;
      this._lastScrollTop = scrollTop;
      this._lastScrollHeight = scrollHeight;

      if (this._programmaticScroll > 0) return;

      this._lastUserScrollAt = Date.now();

      const distanceFromBottom = scrollHeight - scrollTop - sc.clientHeight;
      if (distanceFromBottom <= STICKY_THRESHOLD_PX) this._stickToBottom = true;
      else if (movedUp) this._stickToBottom = false;

      if (sc.scrollTop <= TOP_LOAD_MORE_THRESHOLD_PX && !this.isLoading) {
        this.loadMoreMessages();
      }
    },

    scheduleScrollToBottom() {
      if (this._scrollScheduled) return;
      this._scrollScheduled = true;
      requestAnimationFrame(() => {
        this._scrollScheduled = false;
        this.scrollToBottom();
      });
    },

    scrollToBottom() {
      const sc = this.scrollContainer;
      if (!sc) return;
      const target = sc.scrollHeight - sc.clientHeight;
      // Skip the write entirely if we're already at the bottom — avoids
      // gratuitous scrollbar flashes when nothing actually needs to move.
      if (target - sc.scrollTop <= 1) return;

      this._programmaticScroll++;
      sc.scrollTop = target;
      requestAnimationFrame(() => {
        this._programmaticScroll = Math.max(0, this._programmaticScroll - 1);
      });
    },

    loadMoreMessages() {
      this.isLoading = true;

      const scrollContainer = this.scrollContainer;
      const currentScrollTop = scrollContainer.scrollTop;
      const currentScrollHeight = scrollContainer.scrollHeight;

      this.pushEventTo(this.el, 'load_more', {});

      this.preserveScrollPosition(scrollContainer, currentScrollTop, currentScrollHeight);
    },

    preserveScrollPosition(scrollContainer, currentScrollTop, currentScrollHeight) {
      const observer = new MutationObserver(() => {
        const newScrollHeight = scrollContainer.scrollHeight;
        if (newScrollHeight > currentScrollHeight) {
          const heightDifference = newScrollHeight - currentScrollHeight;

          this._programmaticScroll++;
          scrollContainer.scrollTop = currentScrollTop + heightDifference;
          requestAnimationFrame(() => {
            this._programmaticScroll = Math.max(0, this._programmaticScroll - 1);
          });

          observer.disconnect();
          this.isLoading = false;
        }
      });

      observer.observe(scrollContainer, {
        childList: true,
        subtree: true,
        attributes: false,
      });

      // Fallback timeout in case MutationObserver doesn't trigger.
      setTimeout(() => {
        observer.disconnect();
        this.isLoading = false;
      }, 2000);
    },

    scrollableParent(el) {
      let node = el.parentElement;
      while (node && node !== document.body && node !== document.documentElement) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
          return node;
        }
        node = node.parentElement;
      }
      return el.parentElement;
    },

    destroyed() {
      if (this.scrollContainer && this._boundHandleScroll) {
        this.scrollContainer.removeEventListener('scroll', this._boundHandleScroll);
      }
      if (this._mutationObserver) this._mutationObserver.disconnect();
      if (this._resizeObserver) this._resizeObserver.disconnect();
    },
  },
};
